import { FC, SVGProps } from 'react'
import moment from 'moment'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar-line-16.svg'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/Globe.svg'
import { ReactComponent as TotalChatsIcon } from 'src/assets/svg/inbox-analysis-total-chats.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-outline.svg'
import { ReactComponent as TotalMessagesIcon } from 'src/assets/svg/total-messages-inbox-analysis.svg'
import { ReactComponent as WhatsappIcon } from 'src/assets/svg/whatsapp.svg'
import { OperatorAnalyticsOperator } from 'src/services/__generated__/api'
import styles from './operatorCard.module.scss'

interface OperatorCardProps {
  operator: OperatorAnalyticsOperator
}

const RATING_LABELS: Record<
  NonNullable<OperatorAnalyticsOperator['rating']>,
  string
> = {
  excellent: 'Excellent',
  good: 'Good',
  needs_attention: 'Needs Attention',
}

const SPEED_LABELS: Record<
  NonNullable<OperatorAnalyticsOperator['speed']>,
  string
> = {
  fast: '# Fast',
  slow: '# Slow',
  steady: '# Steady',
}

const PLATFORM_ICONS: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  whatsapp: WhatsappIcon,
}

// Sentiment palette used across the inbox pages.
const SENTIMENTS = [
  { color: '#8080ff', key: 'positive', label: 'Positive' },
  { color: '#c7ced1', key: 'neutral', label: 'Neutral' },
  { color: '#ff6833', key: 'negative', label: 'Negative' },
] as const

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

const percentOf = (part: number, total: number) =>
  total > 0 ? Math.round((part / total) * 100) : 0

export const OperatorCard: FC<OperatorCardProps> = ({ operator }) => {
  const sentimentTotal =
    operator.sentiment.positive +
    operator.sentiment.neutral +
    operator.sentiment.negative
  const PlatformIcon =
    (operator.topPlatform && PLATFORM_ICONS[operator.topPlatform.id]) ||
    GlobeIcon

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.avatar}>{initialsOf(operator.name)}</span>
        <span className={styles.name}>{operator.name}</span>
        {operator.rating && (
          <span className={`${styles.rating} ${styles[operator.rating]}`}>
            {RATING_LABELS[operator.rating]}
          </span>
        )}
      </div>

      <div className={styles.tiles}>
        <div className={styles.tile}>
          <span className={styles.tileLabel}>
            <TotalChatsIcon className={styles.tileIcon} />
            Total Chats
          </span>
          <span className={styles.tileValue}>
            {operator.totalChats.toLocaleString('en-US')}
          </span>
        </div>
        <div className={styles.tile}>
          <span className={styles.tileLabel}>
            <TotalMessagesIcon className={styles.tileIcon} />
            Total Messages
          </span>
          <span className={styles.tileValue}>
            {operator.totalMessages.toLocaleString('en-US')}
          </span>
        </div>
      </div>

      <div className={styles.sentiment}>
        <div className={styles.sentimentHeader}>
          <span className={styles.mutedLabel}>Sentiment</span>
          <span className={styles.positiveShare}>
            <strong>
              {percentOf(operator.sentiment.positive, sentimentTotal)}%
            </strong>{' '}
            positive
          </span>
        </div>
        <div className={styles.barWrap}>
          <div className={styles.bar}>
            {SENTIMENTS.map(({ color, key }) =>
              operator.sentiment[key] > 0 ? (
                <span
                  className={styles.segment}
                  key={key}
                  style={{
                    backgroundColor: color,
                    flexGrow: operator.sentiment[key],
                  }}
                />
              ) : null
            )}
          </div>
          <div className={styles.tooltip}>
            {SENTIMENTS.map(({ color, key, label }) => (
              <div className={styles.tooltipRow} key={key}>
                <span
                  className={styles.tooltipDot}
                  style={{ backgroundColor: color }}
                />
                {percentOf(operator.sentiment[key], sentimentTotal)}% {label}
                <strong>
                  {operator.sentiment[key].toLocaleString('en-US')}
                </strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.divider} />

      <div className={styles.tiles}>
        <div className={styles.tile}>
          <span className={styles.tileLabel}>Response time</span>
          <span className={styles.tileValueRow}>
            <span className={styles.tileValue}>
              {operator.responseTime === null
                ? '–'
                : `${operator.responseTime} min`}
            </span>
            {operator.speed && (
              <span className={`${styles.speed} ${styles[operator.speed]}`}>
                {SPEED_LABELS[operator.speed]}
              </span>
            )}
          </span>
        </div>
        <div className={styles.tile}>
          <span className={styles.tileLabel}>Follow-up rate</span>
          <span className={styles.tileValue}>
            {operator.followUpRate === null ? '–' : `${operator.followUpRate}%`}
          </span>
        </div>
      </div>

      {(operator.topPlatform || operator.topDay) && (
        <div className={styles.section}>
          <span className={styles.mutedLabel}>Top Platform &amp; Top Day</span>
          <div className={styles.chips}>
            {operator.topPlatform && (
              <span className={`${styles.chip} ${styles.platformChip}`}>
                <PlatformIcon className={styles.chipIcon} />
                {operator.topPlatform.name}
              </span>
            )}
            {operator.topDay && (
              <span className={`${styles.chip} ${styles.dayChip}`}>
                <CalendarIcon className={styles.chipIcon} />
                {operator.topDay}
              </span>
            )}
          </div>
        </div>
      )}

      {/* AI sentence (smart-insights-analysis --operators). It covers its own
          fixed window, not the picked dates — it holds no numbers, and the
          label says which days it is based on. */}
      {operator.insight && (
        <div className={styles.section}>
          <span className={styles.insightHeader}>
            <span className={styles.mutedLabel}>Insights</span>
            {operator.insightWindow && (
              <span className={styles.insightWindow}>
                Based on{' '}
                {moment.utc(operator.insightWindow.from).format('MMM D')} –{' '}
                {moment.utc(operator.insightWindow.to).format('MMM D')}
              </span>
            )}
          </span>
          <div className={styles.insight}>
            <LightbulbIcon className={styles.insightIcon} />
            <p>{operator.insight}</p>
          </div>
        </div>
      )}
    </div>
  )
}
